import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { getClients } from '../../../services/clientService';
import { getAppointmentTypes } from '../../../services/appointmentService';

const individualSchema = z.object({
  isGroup: z.literal(false).default(false),
  clientId: z.string().uuid('Select a client'),
  clientIds: z.array(z.string()).optional(),
  maxCapacity: z.coerce.number().int().min(1).max(500).optional().or(z.literal('')),
  appointmentTypeId: z.string().optional().or(z.literal('')),
  date: z.string().min(1, 'Date is required'),
  time: z.string().min(1, 'Time is required'),
  durationMinutes: z.coerce.number().int().min(5).max(480),
  isTelehealth: z.boolean().default(false),
  location: z.string().max(255).optional().or(z.literal('')),
  telehealthLink: z.string().max(500).optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  recurrenceRule: z.enum(['none', 'weekly', 'biweekly', 'monthly']).default('none'),
  recurrenceEndDate: z.string().optional().or(z.literal('')),
  status: z.string().optional(),
  cancellationReason: z.string().optional().or(z.literal('')),
});

const groupSchema = z.object({
  isGroup: z.literal(true),
  clientId: z.string().optional().or(z.literal('')),
  clientIds: z.array(z.string().uuid()).min(1, 'Select at least one client'),
  maxCapacity: z.coerce.number().int().min(1).max(500).optional().or(z.literal('')),
  appointmentTypeId: z.string().optional().or(z.literal('')),
  date: z.string().min(1, 'Date is required'),
  time: z.string().min(1, 'Time is required'),
  durationMinutes: z.coerce.number().int().min(5).max(480),
  isTelehealth: z.boolean().default(false),
  location: z.string().max(255).optional().or(z.literal('')),
  telehealthLink: z.string().max(500).optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  recurrenceRule: z.enum(['none', 'weekly', 'biweekly', 'monthly']).default('none'),
  recurrenceEndDate: z.string().optional().or(z.literal('')),
  status: z.string().optional(),
  cancellationReason: z.string().optional().or(z.literal('')),
});

const schema = z.discriminatedUnion('isGroup', [individualSchema, groupSchema]);

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

const STATUSES = ['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show', 'rescheduled'];

export default function AppointmentForm({ defaultValues = {}, onSubmit, onCancel, isSubmitting, isEdit = false }) {
  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 500 }],
    queryFn: () => getClients({ limit: 500 }),
  });
  const { data: typesData } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: getAppointmentTypes,
  });

  const clients = useMemo(() => clientsData?.clients ?? [], [clientsData]);
  const types = useMemo(() => typesData?.appointmentTypes ?? [], [typesData]);

  // Determine initial group mode from defaultValues
  const [isGroupMode, setIsGroupMode] = useState(defaultValues.isGroup ?? false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      isGroup: false,
      isTelehealth: false,
      durationMinutes: 90,
      recurrenceRule: 'none',
      clientIds: [],
      ...defaultValues,
    },
  });

  const selectedTypeId = watch('appointmentTypeId');
  const isTelehealth = watch('isTelehealth');
  const status = watch('status');
  const recurrenceRule = watch('recurrenceRule');
  const clientIds = watch('clientIds') ?? [];

  // Sync isGroup hidden field with local state
  useEffect(() => {
    setValue('isGroup', isGroupMode);
  }, [isGroupMode, setValue]);

  // Re-sync select values after async data loads
  useEffect(() => {
    if (defaultValues.clientId && clients.length > 0) {
      setValue('clientId', defaultValues.clientId);
    }
  }, [clients, defaultValues.clientId, setValue]);

  useEffect(() => {
    if (types.length === 0) return;
    if (defaultValues.appointmentTypeId) {
      setValue('appointmentTypeId', defaultValues.appointmentTypeId);
    } else if (!isEdit) {
      // Auto-select the first active appointment type when creating a new appointment
      const firstActive = types.find((t) => t.isActive !== false) ?? types[0];
      if (firstActive) setValue('appointmentTypeId', firstActive.id);
    }
  }, [types, defaultValues.appointmentTypeId, isEdit, setValue]);

  // Auto-fill duration when appointment type changes
  useEffect(() => {
    if (!selectedTypeId) return;
    const type = types.find((t) => t.id === selectedTypeId);
    if (type) setValue('durationMinutes', type.durationMinutes);
  }, [selectedTypeId, types, setValue]);

  const toggleClient = (clientId) => {
    const current = getValues('clientIds') ?? [];
    if (current.includes(clientId)) {
      setValue('clientIds', current.filter((id) => id !== clientId));
    } else {
      setValue('clientIds', [...current, clientId]);
    }
  };

  const handleSubmitForm = (data) => {
    const startTime = `${data.date}T${data.time}:00`;
    const common = {
      appointmentTypeId: data.appointmentTypeId || null,
      startTime,
      durationMinutes: data.durationMinutes,
      isTelehealth: data.isTelehealth,
      location: data.location || null,
      telehealthLink: data.telehealthLink || null,
      notes: data.notes || null,
      ...(!isEdit && {
        recurrenceRule: data.recurrenceRule || 'none',
        recurrenceEndDate: data.recurrenceRule !== 'none' ? data.recurrenceEndDate || null : null,
      }),
      ...(isEdit && { status: data.status, cancellationReason: data.cancellationReason || null }),
    };

    if (data.isGroup) {
      onSubmit({
        ...common,
        isGroup: true,
        clientIds: data.clientIds,
        maxCapacity: data.maxCapacity ? parseInt(data.maxCapacity, 10) : null,
      });
    } else {
      onSubmit({
        ...common,
        clientId: data.clientId,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-5">
      {/* Group toggle (create mode only) */}
      {!isEdit && (
        <div className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Session type:</span>
          <div className="flex items-center rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setIsGroupMode(false)}
              className={`px-3 py-1.5 transition-colors ${
                !isGroupMode
                  ? 'bg-primary-600 text-white font-medium'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
              }`}
            >
              Individual
            </button>
            <button
              type="button"
              onClick={() => setIsGroupMode(true)}
              className={`px-3 py-1.5 border-l border-neutral-200 dark:border-neutral-700 transition-colors ${
                isGroupMode
                  ? 'bg-primary-600 text-white font-medium'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
              }`}
            >
              Group
            </button>
          </div>
        </div>
      )}

      {/* Client selection */}
      {isGroupMode ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Appointment type" error={errors.appointmentTypeId}>
              <select className="input" {...register('appointmentTypeId')}>
                <option value="">None</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.cptCode ? ` (${t.cptCode})` : ''}</option>
                ))}
              </select>
            </Field>
            <Field label="Max capacity (optional)" error={errors.maxCapacity}>
              <input type="number" className="input" min={1} max={500} placeholder="e.g. 12" {...register('maxCapacity')} />
            </Field>
          </div>
          <Field label={`Group members * (${clientIds.length} selected)`} error={errors.clientIds}>
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
              {clients.length === 0 ? (
                <p className="text-sm text-neutral-400 p-3">Loading clients…</p>
              ) : (
                clients.map((c) => {
                  const checked = clientIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 ${
                        checked ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleClient(c.id)}
                        className="h-4 w-4 rounded border-neutral-300 text-primary-600"
                      />
                      <span className="text-sm text-neutral-800 dark:text-neutral-200">
                        {c.lastName}, {c.firstName}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Client *" error={errors.clientId}>
            <select className="input" {...register('clientId')}>
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.lastName}, {c.firstName}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Appointment type" error={errors.appointmentTypeId}>
            <select className="input" {...register('appointmentTypeId')}>
              <option value="">None</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.cptCode ? ` (${t.cptCode})` : ''}</option>
              ))}
            </select>
            {selectedTypeId && (() => {
              const t = types.find((x) => x.id === selectedTypeId);
              return t?.cptCode ? (
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  CPT: <span className="font-mono font-medium text-blue-600 dark:text-blue-400">{t.cptCode}</span>
                </p>
              ) : null;
            })()}
          </Field>
        </div>
      )}

      {/* Date, Time, Duration */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Date *" error={errors.date}>
          <input type="date" className="input" {...register('date')} />
        </Field>
        <Field label="Start time *" error={errors.time}>
          <input type="time" step="900" className="input" {...register('time')} />
        </Field>
        <Field label="Duration (min) *" error={errors.durationMinutes}>
          <input type="number" className="input" min={5} max={480} step={5} {...register('durationMinutes')} />
        </Field>
      </div>

      {/* Telehealth */}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="isTelehealth" className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600" {...register('isTelehealth')} />
        <label htmlFor="isTelehealth" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Telehealth session</label>
      </div>

      {isTelehealth ? (
        <Field label="Telehealth link" error={errors.telehealthLink}>
          <input type="url" className="input" placeholder="https://meet.example.com/..." {...register('telehealthLink')} />
        </Field>
      ) : (
        <Field label="Location" error={errors.location}>
          <input type="text" className="input" placeholder="Office address or room" {...register('location')} />
        </Field>
      )}

      {/* Status (edit mode only) */}
      {isEdit && (
        <>
          <Field label="Status" error={errors.status}>
            <select className="input" {...register('status')}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </Field>
          {status === 'cancelled' && (
            <Field label="Cancellation reason" error={errors.cancellationReason}>
              <textarea className="input" rows={2} {...register('cancellationReason')} />
            </Field>
          )}
        </>
      )}

      {/* Notes */}
      <Field label="Notes" error={errors.notes}>
        <textarea className="input" rows={3} placeholder="Session notes or instructions…" {...register('notes')} />
      </Field>

      {/* Recurrence (create mode only) */}
      {!isEdit && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-4">
          <Field label="Repeat" error={errors.recurrenceRule}>
            <select className="input" {...register('recurrenceRule')}>
              <option value="none">Does not repeat</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </Field>
          {recurrenceRule && recurrenceRule !== 'none' && (
            <Field label="End date (optional)" error={errors.recurrenceEndDate}>
              <input type="date" className="input" {...register('recurrenceEndDate')} />
            </Field>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        )}
        <button type="submit" disabled={isSubmitting} className="btn btn-primary disabled:opacity-60">
          {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Book appointment'}
        </button>
      </div>
    </form>
  );
}
